import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { Card } from '@monorepo/base/src/components/card/card';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import { Decisions } from '../../../../enums/decisions';
import { IAdminxStore } from '../../../../stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { Tabs } from '@monorepo/base/src/components/tabs/tabs';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { Flex } from '@monorepo/base/src/components/flex/flex';
import { Dropdown, option } from '@monorepo/base/src/components/dropdown/dropdown';
import { StrategyModel } from '../../../../models/strategy.model';
import { Budget } from '../budget/budget';
import { DealTypes } from '../../../../enums/deal-types';
import { ALL_PHASE, IEntity } from '../../../../models/campaign.model';
import { ActionModal } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import styles from '../../../../hooks/theaders/campaigns/campaigns.headers.module.scss';
import { FormError } from '@monorepo/base/src/components/form/form-error/form-error';
import {
	defaultStrateryForPreferredDealType,
	defaultStrategyForPrivateAuctionType,
	defaultStrategyForGuaranteedDealType,
} from '../deal-type-strategies';

export const Phases = observer(() => {
	const [currentPhaseId, setCurrentPhaseId] = useState<id>();

	const { campaignStore, modalsStore } = useStores<IAdminxStore>();
	const campaignCrudStore = campaignStore.getCrud();
	const campaign = campaignCrudStore.getData();

	const formStore = campaignCrudStore.getFormStore();
	const inputOptions = campaignStore.getInputOptions();
	const strategies = inputOptions.getStrategies();
	const firstPhaseId = campaign.getPhases()?.[0]?.phase.id;
	const rules = campaign.getRules(currentPhaseId);
	const maxLoss = rules?.getMaxLoss();
	const maxWinsPerClick = rules?.getMaxWinsPerClick();
	const maxWinsPerConversion = rules?.getMaxWinsPerConversion();
	const maxClicksPerConversion = rules?.getMaxClicksPerConversion();
	const maxCPM = rules?.getMaxCPM();
	const currentPhase = campaign.getPhaseById(currentPhaseId);
	const dailyDomainMaxLoss = rules?.getDailyDomainMaxLoss();

	// set active tab to first phase on load
	useEffect(() => {
		if (currentPhaseId !== firstPhaseId && firstPhaseId && !currentPhaseId) {
			setCurrentPhaseId(firstPhaseId);
		}
	}, [currentPhaseId, firstPhaseId]);

	// reset active tab after phase reset
	useEffect(() => {
		if (!campaign.getPhases().some(p => p.phase.id === currentPhaseId)) {
			setCurrentPhaseId(firstPhaseId);
		}
	}, [campaign.getPhases()]);

	useEffect(() => {
		const auctionType = campaign.getAuctionType();
		const name =
			auctionType === DealTypes.PreferredDeal
				? defaultStrateryForPreferredDealType
				: auctionType === DealTypes.GuaranteedDeal
					? defaultStrategyForGuaranteedDealType
					: auctionType === DealTypes.PrivateAuction
						? defaultStrategyForPrivateAuctionType
						: undefined;
		if (!name) {
			return;
		}
		const strategy = strategies.find(s => s.name === name);
		if (!strategy) {
			return;
		}
		campaign.getPhases().forEach(p => p.setStrategy(new StrategyModel(strategy)));
	}, [campaign.getAuctionType(), strategies]);

	const handleAddTab = (phase: IEntity) => {
		const add = () => {
			campaign.addPhase(phase);
			setCurrentPhaseId(phase.id);

			if (campaign.getAuctionType() === DealTypes.PreferredDeal) {
				const strategy = strategies.find(_strategy => _strategy.name === defaultStrateryForPreferredDealType);

				if (strategy) {
					campaign.getPhases().forEach(p => p.setStrategy(new StrategyModel(strategy)));
				}
			}

			if (campaign.getAuctionType() === DealTypes.PrivateAuction) {
				const strategy = strategies.find(_strategy => _strategy.name === defaultStrategyForPrivateAuctionType);
				if (strategy) {
					campaign.getPhases().forEach(p => p.setStrategy(new StrategyModel(strategy)));
				}
			}
			if (campaign.getAuctionType() === DealTypes.GuaranteedDeal) {
				const strategy = strategies.find(_strategy => _strategy.name === defaultStrategyForGuaranteedDealType);
				if (strategy) {
					campaign.getPhases().forEach(p => p.setStrategy(new StrategyModel(strategy)));
				}
			}
		};

		if (currentPhaseId === ALL_PHASE.id) {
			return modalsStore.addModal({
				children: (
					<ActionModal
						className={styles.actionModal}
						childrenWrapperClassName={styles.actionModalChildrenWrapper}
						primaryBtnTitle="Save"
						onAction={() => {
							campaign.setPhase({
								...currentPhase,
								active: false,
							});

							add();
						}}>
						<h3>Adding KNOWN or NEW phase will deactivate the "ALL" phase.</h3>
						<p>Are you sure you want to proceed?</p>
					</ActionModal>
				),
			});
		}

		add();
	};

	return (
		<Card id={'phases'} title={'Phases'}>
			<Spacer y={2} />

			<Tabs
				addOptions={campaign.getAvailablePhases(campaignStore.getInputOptions().getPhases())}
				onTabChange={setCurrentPhaseId}
				addTabPlaceholder="Please add a phase"
				onAddTab={handleAddTab}
				onRemoveTab={id => campaign.removePhase(id)}
				activeTabId={currentPhaseId}
				showActive
				showAddTab={!!campaign.getAvailablePhases(campaignStore.getInputOptions().getPhases()).length}
				tabs={campaign.getPhases()?.map(p => ({
					...p.phase,
					active: p.active,
					deletable: !p.id,
				}))}
			/>

			<Spacer y={2} />

			{!!currentPhase && (
				<React.Fragment>
					<Flex justifyContent="flex-start">
						<Toggle
							title="Is phase active?"
							label={currentPhase?.active ? Decisions.Yes : Decisions.No}
							onChange={active => campaign?.setPhase({ ...currentPhase, active })}
							disabled={campaign.getPhases().length > 1 && currentPhase?.phase.id === ALL_PHASE.id}
							defaultValue={currentPhase?.active}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'is_phase_active')] }}
						/>
					</Flex>

					<Spacer />

					<Budget currentPhase={currentPhase} style={{ width: '410px' }} />

					<Spacer />

					<Card title={'Cache & bid key'} style={{ width: '410px' }}>
						<Dropdown
							label={'Select Cache & bid Key Generator'}
							required={true}
							defaultOption={currentPhase.getCacheKeyGenerator()}
							options={inputOptions.getKeyGenerators()}
							onSelect={(cacheKeyGenerator: Set<option> | undefined) => {
								const val = cacheKeyGenerator?.values().next().value;
								if (val) {
									currentPhase.setCacheKeyGenerator(val);
								}
								formStore.clearError('cache_key_generator_phases');
							}}
							error={formStore.getErrors().get('cache_key_generator_phases')?.getMsg()}
							debugProps={{
								dataAttrs: [new DataAttribute('id', `cache_key_generator_phase_${currentPhase.phase.id}`)],
							}}
						/>
					</Card>

					<Spacer />

					<Card title={'Strategy'} icon={'lightbulb-02'} style={{ width: '410px' }}>
						<Dropdown
							required={true}
							defaultOption={
								currentPhase?.strategy ? `${currentPhase.strategy.name} (${currentPhase.strategy.getId()})` : undefined
							}
							disabled={
								campaign.getAuctionType() === DealTypes.PreferredDeal ||
								campaign.getAuctionType() === DealTypes.PrivateAuction ||
								campaign.getAuctionType() === DealTypes.GuaranteedDeal
							}
							label={'Select Strategy'}
							options={strategies.map(_strategy => `${_strategy.name} (${_strategy.getId()})`)}
							onSelect={(val: Set<option> | undefined) => {
								const selectedLabel = val?.values().next().value;
								const strategy = strategies.find(_strategy => `${_strategy.name} (${_strategy.getId()})` === selectedLabel);

								if (strategy) {
									currentPhase?.setStrategy(new StrategyModel(strategy));
								}
							}}
							autocomplete={true}
							tooltip={true}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'strategy')] }}
							isKeyboardNavigation={true}
						/>
						{!!formStore.getErrors().get('strategy')?.getMsg() && (
							<React.Fragment>
								<Spacer />
								<FormError msg={formStore.getErrors().get('strategy')?.getMsg()} />
							</React.Fragment>
						)}
					</Card>

					<Spacer />

					<Card title={'Rules'} icon={'gavel'} style={{ width: '410px' }}>
						<Spacer />

						<Input
							type={'number'}
							icon={'currency-dollar'}
							value={maxLoss?.toString()}
							min={0}
							placeholder={'Max Loss (per key)'}
							onValue={value => rules?.setMaxLoss(parseInt(value))}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'max_loss')] }}
						/>
						<Input
							type={'number'}
							value={maxWinsPerClick?.toString()}
							placeholder={'Max wins per click'}
							onValue={value => rules?.setMaxWinsPerClick(parseInt(value))}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'max_wins_per_click')] }}
						/>
						<Input
							type={'number'}
							value={maxWinsPerConversion?.toString()}
							placeholder={'Max wins per conversion'}
							onValue={value => rules?.setMaxWinsPerConversion(parseInt(value))}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'max_wins_per_conversion')] }}
						/>
						<Input
							type={'number'}
							value={maxClicksPerConversion?.toString()}
							placeholder={'Max clicks per conversion'}
							onValue={value => rules?.setMaxClicksPerConversion(parseInt(value))}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'max_clicks_per_conversion')] }}
						/>
						<Input
							type={'number'}
							value={maxCPM?.toString()}
							placeholder={'Max CPM'}
							onValue={value => rules?.setMaxCPM(parseFloat(value))}
							step={'0.1'}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'max_cpm')] }}
						/>

						<Input
							type="number"
							icon="currency-dollar"
							value={dailyDomainMaxLoss?.toString() || '0'}
							placeholder="Daily Domain Max Loss"
							onValue={value => rules?.setDailyDomainMaxLoss(parseInt(value))}
							bottomLabel="For app traffic  -  domain max loss should be left empty"
							debugProps={{ dataAttrs: [new DataAttribute('id', 'daily_domain_max_loss')] }}
						/>

						<Spacer />

						<Flex alignItems="start" flexDirection="column" gap="16px">
							<Toggle
								title={'Use Optimal Gain For Max Loss Calculation?'}
								label={rules?.getUseOptimalGainForMaxLossCalculation() ? Decisions.Yes : Decisions.No}
								onChange={status => rules?.setUseOptimalGainForMaxLossCalculation(status)}
								defaultValue={rules?.getUseOptimalGainForMaxLossCalculation()}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'use_optimal_gain_for_max_loss_calculation')] }}
							/>

							<Toggle
								title={'Use Domain Budget Limit?'}
								label={rules?.getUseDomainBudgetLimit() ? Decisions.Yes : Decisions.No}
								onChange={status => rules?.setUseDomainBudgetLimit(status)}
								defaultValue={rules?.getUseDomainBudgetLimit()}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'domain_budget_limit')] }}
							/>
						</Flex>
					</Card>
					<Spacer />
				</React.Fragment>
			)}

			{!!formStore.getErrors().get('campaign_phases')?.getMsg() && (
				<React.Fragment>
					<FormError msg={formStore.getErrors().get('campaign_phases')?.getMsg()} />
					<Spacer />
				</React.Fragment>
			)}
		</Card>
	);
});
